/**
 * Frontend module that overrides the catalog plugin's index page with our
 * CustomCatalogPage (edit-gated table) and the about entity card with
 * EntityAboutCardWithEditGate so Edit is only shown when allowed.
 *
 * Also registers the Relations (catalog graph) card on entity overview — the
 * alpha catalog UI does not use legacy EntityPage.tsx, so this must be wired
 * here (same extension point as the default catalog-graph alpha plugin).
 *
 * Relations card uses plain createExtension (sync) instead of EntityCardBlueprint +
 * ExtensionBoundary.lazy, which loads zod/zod-to-json-schema and has caused browser
 * SyntaxError in some setups.
 */

import { createElement } from 'react';
import {
  createExtension,
  createFrontendModule,
  coreExtensionData,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { EntityAboutCardWithEditGate } from './components/catalog/EntityAboutCardWithEditGate';
import { EntityRelationsOverviewCard } from './components/catalog/EntityRelationsOverviewCard';

const customCatalogPageExtension = PageBlueprint.make({
  params: {
    path: '/catalog',
    routeRef: catalogPlugin.routes.catalogIndex,
    loader: () =>
      import('./components/catalog/CustomCatalogPage').then(m =>
        createElement(m.CustomCatalogPage),
      ),
  },
});

const aboutCardOverrideExtension = createExtension({
  kind: 'entity-card',
  name: 'about',
  attachTo: { id: 'entity-content:catalog/overview', input: 'cards' },
  output: [coreExtensionData.reactElement],
  factory: () => [
    coreExtensionData.reactElement(
      createElement(EntityAboutCardWithEditGate, { variant: 'gridItem' }),
    ),
  ],
});

/** Relations graph on overview; uses fixed `/catalog-graph` link (see EntityRelationsOverviewCard). */
const relationsCardExtension = createExtension({
  kind: 'entity-card',
  name: 'relations',
  attachTo: { id: 'entity-content:catalog/overview', input: 'cards' },
  output: [coreExtensionData.reactElement],
  factory: () => [
    coreExtensionData.reactElement(
      compatWrapper(
        createElement(EntityRelationsOverviewCard, {
          variant: 'gridItem',
          height: 400,
        }),
      ),
    ),
  ],
});

export const catalogPageOverrideModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    customCatalogPageExtension,
    aboutCardOverrideExtension,
    relationsCardExtension,
  ],
});
